import {
  AvailableCoupon,
  CartProduct,
  Coupon,
  Discount,
  ProductRecord
} from '@shared/cart-js/src/schemas';
import { isEmpty, isUndefined } from 'lodash';
import { SavedCart, SavedCartTotals } from './schemas';

type CartProductSku = string;
type DiscountId = string;
type PromoCodeId = string;

type ProductDiscountedAmounts = Record<CartProductSku, number>;
type CouponProductDiscounts = Record<DiscountId, ProductDiscountedAmounts>;
type PromoDiscountTotal = Record<
  PromoCodeId,
  {
    discounts: CouponProductDiscounts;
    couponCode: string;
    description: string;
    couponCodeId: string;
    couponCodeDisplayText: string;
    totalSavings: number;
    appliesTo: (
      | 'freeMonths'
      | 'subscription'
      | 'product'
      | 'total'
      | 'order'
    )[];
  }
>;

type Breakdown = {
  discounts: CouponProductDiscounts;
  couponCode: string;
  description: string;
  couponCodeId: string;
  couponCodeDisplayText: string;
  totalSavings: number;
  appliesTo: ('freeMonths' | 'subscription' | 'product' | 'total' | 'order')[];
};

type AvailableCouponBreakdown = Breakdown & {
  showOnCustomerInfoPage: boolean;
  showOnPaymentPage: boolean;
  disclaimer?: string;
};

const calculateCartDiscountAmounts = ({
  appliedDiscounts: appliedDiscountsIds,
  discounts,
  coupons,
  availableCoupons,
  cartProducts,
  totals,
  orderTotals
}: {
  appliedDiscounts: string[];
  discounts: Array<Discount>;
  coupons: Array<Coupon>;
  availableCoupons: Array<AvailableCoupon>;
  cartProducts: Record<string, CartProduct>;
  totals: Partial<SavedCartTotals>;
  orderTotals?: SavedCart['orderTotals'];
}): {
  breakdown: Array<Breakdown>;
  remove: Array<string>;
  availableCouponsBreakdown: Array<AvailableCouponBreakdown>;
} => {
  const autoAppliedDiscounts = appliedDiscountsIds
    .map((discountId) => {
      const discountApplied = discounts.find((discount) => {
        return discount.id === discountId;
      });
      if (discountApplied) {
        return discountApplied;
      }
      return null;
    })
    .filter(
      (discount) =>
        discount !== null && discount.activateCondition !== 'couponCode'
    ) as Array<Discount>;

  const allDiscounts = autoAppliedDiscounts.map((discount) => discount.id);

  // Map over the cartProducts and grab the discounts associated with the discount in the discountMap
  const promoDiscountTotal: PromoDiscountTotal = {};

  const remove: Array<string> = [];
  const savingsTolerance = 0.001;

  coupons.forEach((coupon) => {
    if (coupon?.discounts?.length === 0) return;
    const couponDiscounts = coupon.discounts
      ?.filter((discount) => appliedDiscountsIds.includes(discount.id))
      .map((couponDiscount) => {
        const discountApplied = discounts.find((discount) => {
          return discount.id === couponDiscount.id;
        });
        if (discountApplied && !allDiscounts.includes(discountApplied.id)) {
          allDiscounts.push(discountApplied.id);
          return discountApplied;
        }
        return null;
      })
      .filter((discount) => discount !== null) as Array<Discount>;

    const relatedDiscounts = extractRelatedDiscounts({
      discounts: couponDiscounts,
      cartProducts,
      totals,
      orderTotals
    });

    const totalSavings = Object.values(relatedDiscounts).reduce(
      (sum = 0, productDiscount) => {
        Object.values(productDiscount).forEach(
          (productDiscount) =>
            (sum =
              Math.floor((sum + productDiscount) * 100 + savingsTolerance) /
              100)
        );
        return sum;
      },
      0
    );

    const appliesTo = couponDiscounts?.map?.(({ appliesTo }) => appliesTo);

    promoDiscountTotal[coupon.id] = {
      couponCode: coupon.couponCode,
      description:
        (coupon?.description?.length ?? 0) > 0
          ? coupon.description ?? ''
          : couponDiscounts?.length > 0
            ? generateDiscountDescription({
                discount: couponDiscounts[0],
                products: cartProducts
              })
            : coupon?.description ?? '',
      couponCodeId: coupon.id,
      couponCodeDisplayText: coupon.couponDisplayText ?? '',
      discounts: relatedDiscounts,
      totalSavings,
      appliesTo: [...new Set(appliesTo)]
    };

    if (couponDiscounts?.length === 0) {
      remove.push(coupon.couponCode);
    }
  });

  // Process availableCoupons and store savings persistently

  const availablePromotions: Record<
    string,
    Breakdown & {
      showOnCustomerInfoPage: boolean;
      showOnPaymentPage: boolean;
      disclaimer?: string;
    }
  > = {};

  availableCoupons.forEach((coupon) => {
    const couponDiscounts = Object.values(coupon.discounts);

    const relatedDiscounts = extractRelatedDiscounts({
      discounts: couponDiscounts,
      cartProducts,
      totals,
      orderTotals
    });

    const totalSavings = Object.values(relatedDiscounts).reduce(
      (sum = 0, productDiscount) => {
        Object.values(productDiscount).forEach(
          (productDiscount) =>
            (sum =
              Math.floor((sum + productDiscount) * 100 + savingsTolerance) /
              100)
        );
        return sum;
      },
      0
    );

    availablePromotions[coupon.couponCode] = {
      couponCode: coupon.couponCode,
      description:
        (coupon?.description?.length ?? 0) > 0
          ? coupon.description ?? ''
          : couponDiscounts?.length > 0
            ? generateDiscountDescription({
                discount: couponDiscounts[0],
                products: cartProducts
              })
            : coupon?.description ?? '',
      couponCodeId: coupon.id,
      couponCodeDisplayText: coupon.couponDisplayText ?? '',
      discounts: relatedDiscounts,
      totalSavings,
      showOnCustomerInfoPage: coupon.showOnCustomerInfoPage,
      showOnPaymentPage: coupon.showOnPaymentPage,
      disclaimer: coupon.disclaimer
    };

    if (couponDiscounts?.length === 0) {
      remove.push(coupon.couponCode);
    }
  });

  const availableCouponsBreakdown = Object.values(availablePromotions);
  const couponApplied = Object.values(promoDiscountTotal);

  return { breakdown: couponApplied, remove, availableCouponsBreakdown };
};

const generateDiscountDescription = ({
  discount,
  products
}: {
  discount: Pick<
    Discount,
    | 'id'
    | 'discountType'
    | 'restrictTo'
    | 'appliesTo'
    | 'maxQuantity'
    | 'freeMonths'
    | 'discountAmount'
  >;
  products: Record<string, CartProduct>;
}) => {
  const {
    discountType,
    restrictTo,
    appliesTo,
    maxQuantity,
    freeMonths,
    discountAmount
  } = discount;

  const percent = discountType == 'percentage';

  const descriptionMap: { [key: string]: string } = {
    product: 'equipment',
    freeMonths: 'monitoring',
    order: 'order',
    total: 'order',
    '': ''
  };

  const offWhat =
    restrictTo.length === 1 && products
      ? products?.[restrictTo[0]]?.name || ''
      : descriptionMap[appliesTo];

  const isFree = percent && discountAmount === 100;
  const amountOff = `${
    percent ? `${discountAmount}%` : `$${discountAmount}`
  } off`;

  const description =
    freeMonths && freeMonths > 0 && restrictTo.length === 1
      ? `${freeMonths} month${freeMonths > 1 ? 's' : ''} of free monitoring`
      : `${
          isFree ? `${maxQuantity ? `${maxQuantity} ` : ''}free` : amountOff
        } ${offWhat}${(maxQuantity || 0) > 1 ? 's' : ''}`;

  return description;
};

const extractRelatedDiscounts = ({
  discounts,
  cartProducts,
  totals,
  orderTotals
}: {
  discounts: Array<
    Pick<
      Discount,
      | 'appliesTo'
      | 'discountType'
      | 'discountAmount'
      | 'freeMonths'
      | 'restrictTo'
      | 'id'
    >
  >;
  cartProducts: Record<string, CartProduct>;
  totals: Partial<SavedCartTotals>;
  orderTotals?: SavedCart['orderTotals'];
}) => {
  const couponProductDiscounts: CouponProductDiscounts = {};

  discounts?.forEach((discount) => {
    const productDiscountedAmounts: ProductDiscountedAmounts = { order: 0 };

    if (discount.appliesTo === 'order' && discount.discountType === 'fixed') {
      productDiscountedAmounts.order += discount.discountAmount;
    }

    if (
      discount.appliesTo === 'order' &&
      discount.discountType === 'percentage'
    ) {
      productDiscountedAmounts.order +=
        orderTotals?.appliedDiscountMap?.[discount.id] ??
        (totals?.orderBaseTotal ?? 0) * (discount.discountAmount / 100);
    }

    const applicableProducts =
      discount.restrictTo?.length > 0
        ? discount.restrictTo
        : Object.keys(cartProducts);

    applicableProducts.forEach((sku) => {
      const product = cartProducts[sku];

      if (!product) return;
      let totalDiscount = product.discountMap?.[discount.id] || 0;

      // Calculate Free Months of Monitoring
      if (['vm-pn', 'bm-pn'].includes(sku)) {
        const freeMonthsDiscount =
          Math.round(discount.freeMonths * product.grandTotal * 100) / 100;
        totalDiscount = freeMonthsDiscount;
      }

      if (totalDiscount > 0) {
        productDiscountedAmounts[sku] = totalDiscount;
      }
    });

    couponProductDiscounts[discount.id] = productDiscountedAmounts;
  });

  return couponProductDiscounts;
};

const generateDiscountBadge = (discount: Discount, product: ProductRecord) => {
  const { discountType, discountAmount, maxQuantity } = discount;
  const qty = maxQuantity ?? product?.qty ?? 0;
  if (discountType === 'percentage') {
    if (discountAmount === 100 && qty > 0) {
      return `${qty} Free`;
    }
    return `${discountAmount}% off`;
  }

  if (discountType === 'fixed') return `$${discountAmount} off`;
};
type DiscountBreakdown = ReturnType<
  typeof calculateCartDiscountAmounts
>['breakdown'];

type AvailableCouponsBreakdown = ReturnType<
  typeof calculateCartDiscountAmounts
>['availableCouponsBreakdown'];

const missingInitialProducts = ({
  products,
  cartItemIds,
  cartProducts
}: {
  products: SavedCart['products'];
  cartItemIds: SavedCart['cartItemIds'];
  cartProducts: SavedCart['cartProducts'];
}) => {
  const missingProducts =
    !isUndefined(products['CPA-PANEL-345-1']) &&
    !isUndefined(products['CPA-HUB-345-1']) &&
    !isUndefined(products['CP-YS-23i-1']) &&
    !isUndefined(products['window-sticker-sm']) &&
    !isEmpty(products['CPA-PANEL-345-1']) &&
    !isEmpty(products['CPA-HUB-345-1']) &&
    !isEmpty(products['CP-YS-23i-1']) &&
    !isEmpty(products['window-sticker-sm']) &&
    (!cartItemIds.includes('CPA-PANEL-345-1') ||
      !cartItemIds.includes('CPA-HUB-345-1') ||
      !cartItemIds.includes('CP-YS-23i-1') ||
      !cartItemIds.includes('window-sticker-sm') ||
      (products?.['CPA-PANEL-345-1']?.qty || 1) < 1 ||
      products['CPA-HUB-345-1'].qty !== 1 ||
      products['CP-YS-23i-1'].qty !== 1 ||
      products['window-sticker-sm'].qty !== 3 ||
      isUndefined(cartProducts['CPA-PANEL-345-1']) ||
      isUndefined(cartProducts['CPA-HUB-345-1']) ||
      isUndefined(cartProducts['CP-YS-23i-1']) ||
      isUndefined(cartProducts['window-sticker-sm']) ||
      isEmpty(cartProducts['CPA-PANEL-345-1']) ||
      isEmpty(cartProducts['CPA-HUB-345-1']) ||
      isEmpty(cartProducts['CP-YS-23i-1']) ||
      isEmpty(cartProducts['window-sticker-sm']) ||
      cartProducts['CPA-PANEL-345-1'].qty < 1 ||
      cartProducts['CPA-HUB-345-1'].qty !== 1 ||
      cartProducts['CP-YS-23i-1'].qty !== 1 ||
      cartProducts['window-sticker-sm'].qty !== 3);

  return missingProducts;
};

export {
  AvailableCouponsBreakdown,
  calculateCartDiscountAmounts,
  DiscountBreakdown,
  generateDiscountBadge,
  missingInitialProducts
};
